/**
 * Problem tags: the "what's asked" axis (LG-55).
 *
 * A problem tag describes what the student must produce, and is constant
 * across every approach to the problem. That distinguishes it from the two
 * axes LG-112 shipped:
 *   - modifiers  — how the code works; VARIES between approaches (Approach)
 *   - overlays   — facts about a concept (SubtopicDefinition)
 * And from the concept axis, SubtopicDefinition, which owns DSA ideas.
 * Anything nameable as a DSA concept belongs in the taxonomy, NOT here.
 *
 * This module is the single source of truth for the vocabulary and for the
 * legacy `Problem.tags` demux that seeded the initial data (LG-55, since
 * removed — see LG-355). The seed (0022) and backfill (0023) migrations
 * mirror these maps in SQL, so changing a slug, name, description, or
 * displayOrder here changes nothing in any database — Drizzle does not diff
 * data, so no migration is generated. Write one by hand and keep it in sync
 * with the seeded rows.
 *
 * Deliberately free of DB imports so scripts and tests can load it cheaply.
 */
import type { ProblemType } from '../../db/schema/enums.js'

/**
 * A row of TagDefinition. Named for the table, not for the ProblemTag junction
 * — `ProblemTag` is already exported from db/schema/problem-tags.ts as the
 * `{ problemId, tagId }` join row, and TagDefinition is deliberately
 * grain-agnostic so a future CodeReviewTag junction can reuse it.
 */
export interface TagDefinitionSeed {
  slug: string
  name: string
  /** Criteria for when this tag applies (tagging guidance). */
  description: string
  displayOrder: number
}

/**
 * The eight launch tags — a controlled vocabulary, not a folksonomy. Adding a
 * value is a deliberate editorial act; it needs tagging guidance a human can
 * apply consistently.
 *
 * `as const satisfies` rather than a `readonly TagDefinitionSeed[]` annotation:
 * the annotation would widen every slug literal to `string` and there would be
 * no `ProblemTagSlug` union to check the demux map against.
 */
export const PROBLEM_TAG_VOCABULARY = [
  {
    slug: 'design',
    name: 'Design',
    description:
      'The student implements a class or API with a specified set of operations, rather than a single function. Corresponds to a class-based test configuration.',
    displayOrder: 10,
  },
  {
    slug: 'game-theory',
    name: 'Game Theory',
    description:
      'Two or more players alternate optimal moves; the answer depends on reasoning about an adversary rather than on a single agent optimising.',
    displayOrder: 20,
  },
  {
    slug: 'data-stream',
    name: 'Data Stream',
    description:
      'Input arrives incrementally and cannot be re-read or held in full; the solution must maintain state across arrivals.',
    displayOrder: 30,
  },
  {
    slug: 'brainteaser',
    name: 'Brainteaser',
    description:
      'The answer turns on a single insight or closed-form observation rather than on applying a data structure or algorithm.',
    displayOrder: 40,
  },
  {
    slug: 'randomized',
    name: 'Randomized',
    description:
      'The solution deliberately uses randomness — random sampling, shuffling, reservoir sampling, or rejection sampling. Correctness is probabilistic or distributional.',
    displayOrder: 50,
  },
  {
    slug: 'iterator',
    name: 'Iterator',
    description:
      'The student implements a traversal interface (hasNext/next or equivalent) whose state advances lazily between calls.',
    displayOrder: 60,
  },
  {
    slug: 'concurrency',
    name: 'Concurrency',
    description:
      'The solution coordinates multiple threads or tasks — ordering, mutual exclusion, or signalling. Same editor and runner as any other coding problem.',
    displayOrder: 70,
  },
  {
    slug: 'probability',
    name: 'Probability & Statistics',
    description:
      'The answer requires computing an expectation, probability, or statistical estimate, rather than a deterministic result.',
    displayOrder: 80,
  },
] as const satisfies readonly TagDefinitionSeed[]

/** The slugs the vocabulary actually declares — the only valid demux targets. */
export type ProblemTagSlug = (typeof PROBLEM_TAG_VOCABULARY)[number]['slug']

/**
 * Legacy `Problem.tags` value -> problem tag slug.
 *
 * Only descriptors with no taxonomy home appear here. DSA concepts (Array,
 * Hash Table, DFS, Prefix Sum, ...) are already mapped to SubtopicDefinition
 * via ProblemSubtopicTag and must NOT be duplicated as problem tags.
 *
 * The three sampling tags collapse into `randomized`: every Reservoir/Rejection
 * Sampling problem already carries Randomized too, so this merge adds no rows.
 */
export const LEGACY_TAG_TO_PROBLEM_TAG: Readonly<
  Record<string, ProblemTagSlug>
> = {
  Design: 'design',
  'Game Theory': 'game-theory',
  'Data Stream': 'data-stream',
  Brainteaser: 'brainteaser',
  Randomized: 'randomized',
  'Reservoir Sampling': 'randomized',
  'Rejection Sampling': 'randomized',
  Iterator: 'iterator',
  Concurrency: 'concurrency',
  'Probability and Statistics': 'probability',
}

/**
 * Legacy `Problem.tags` value -> Problem.type.
 *
 * These two are the only legacy tags that change the coding surface. No problem
 * carries both (verified: overlap = 0), so the type is never ambiguous.
 *
 * `Interactive` is deliberately absent from BOTH maps: it describes how the
 * harness feeds input (a provided API or a class-instance parameter), which
 * TestConfiguration already models. See spec §4.
 */
export const LEGACY_TAG_TO_PROBLEM_TYPE: Readonly<Record<string, ProblemType>> =
  {
    Database: 'sql',
    Shell: 'shell',
  }

/**
 * Derive Problem.type from the legacy `tags` array — the write-path twin of the
 * one-shot backfill (0024). Returns 'algorithms' unless a legacy type tag is
 * present. Throws if Database and Shell co-occur: they are mutually exclusive
 * coding surfaces, and the write path is a better place than the migration to
 * reject the ambiguity loudly (LG-334).
 */
export function deriveProblemType(tags: readonly string[]): ProblemType {
  const matched = Object.entries(LEGACY_TAG_TO_PROBLEM_TYPE).filter(
    ([legacyTag]) => tags.includes(legacyTag),
  )
  if (matched.length > 1) {
    throw new Error(
      `Problem carries mutually-exclusive type tags (${matched
        .map(([legacyTag]) => legacyTag)
        .join(', ')}); Database and Shell must not co-occur.`,
    )
  }
  return matched[0]?.[1] ?? 'algorithms'
}

/**
 * Derive the distinct problem-tag slugs from the legacy `tags` array. DSA
 * concepts and the two type tags are absent from LEGACY_TAG_TO_PROBLEM_TAG, so
 * they contribute nothing here; the three sampling tags collapse to
 * `randomized`. Order follows first appearance in `tags`.
 */
export function deriveProblemTagSlugs(
  tags: readonly string[],
): ProblemTagSlug[] {
  const slugs = new Set<ProblemTagSlug>()
  for (const tag of tags) {
    const slug = LEGACY_TAG_TO_PROBLEM_TAG[tag]
    if (slug) slugs.add(slug)
  }
  return [...slugs]
}
